// View-model of a Markdown pane (design v6 F14–F16; reference mdPaneVals L657–717). The editor itself, autosave
// and versions live in frontend/src/modules/markdown; this only resolves the file/task context from the local
// projection and the pane's persisted UI state (mode, history panel, previewed version).
import type { Pane, Project, Task, TaskList } from '@/domain/model';
import type { PanesController } from './controller';
import type { MdMode, MdTaskContext } from '@/modules/markdown/format';

export interface MdPaneVM {
  isMd: true; label: string | null; fileId: string; taskId: string; projectId: string | null;
  fileName: string | null; task: MdTaskContext | null; mode: MdMode; histOpen: boolean; histView: number | null; canEdit: boolean;
  setMode(m: MdMode): void; setHistOpen(v: boolean): void; setHistView(n: number | null): void;
  closePane(): void; openPop(): void; openProject(): void;
}

export function findTask(projects: Project[], taskId: string | undefined): { p: Project; l: TaskList; t: Task } | null {
  if (!taskId) return null;
  for (const p of projects) for (const l of p.lists) { const t = l.tasks.find(x => x.id === taskId); if (t) return { p, l, t }; }
  return null;
}

export function mdVals(c: PanesController, pane: Pane, projects: Project[]): MdPaneVM {
  const hit = findTask(projects, pane.taskId);
  const f = hit && (hit.t.files || []).find(x => x.id === pane.fileId && !x.pending && !x.error);
  const setPane = (o: Partial<Pane>) => c.setPanes(ps => ps.map(x => (x.id === pane.id ? { ...x, ...o } : x)));
  const t = hit?.t, items = t?.checklist || [];
  const canEdit = !!hit && c.canEditProject(hit.p);
  return {
    isMd: true, label: f ? f.name : null, fileId: pane.fileId || '', taskId: pane.taskId || '', projectId: hit?.p.id ?? null,
    fileName: f ? f.name : null,
    task: hit && t ? { projectName: hit.p.name, listTitle: hit.l.title || null, title: t.title, tags: t.tags || [], checklistDone: items.filter(i => i.done).length,
      checklistTotal: items.length, deadline: t.deadline || null, fileCount: (t.files || []).length } : null,
    mode: (pane.mode || 'split') as MdMode, histOpen: !!pane.histOpen, histView: pane.histView ?? null, canEdit,
    setMode: m => setPane({ mode: m }),
    setHistOpen: v => setPane({ histOpen: v }),
    setHistView: n => setPane({ histView: n }),
    closePane: () => { if (c.state.mdPop === pane.id) c.setState({ mdPop: null }); c.setPanes(ps => ps.filter(x => x.id !== pane.id)); },
    openPop: () => c.setState({ mdPop: pane.id }),
    openProject: () => { if (hit) c.openProject(hit.p.id, true); },
  };
}
